import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {type BaseStoreItem} from '../../../../shared/store/BaseStoreItem.ts'
import {type UserLoginState} from '../../../../shared/UserLoginState.ts'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints.ts'
import {createHeaderIn, createImgIn, createLinkIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils.ts'
import {type ProfileSectionLink, UIProfileLinkController} from './UIProfileLinkController.ts'
import {UIProfileOverviewController} from './UIProfileOverviewController.ts'
import {type ColonistMembershipType, type LobbyImage} from '@colonist/types'
import {ClientUserState} from '../ClientUserState.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {getStoreCategoryString} from '../store/StoreUtils.ts'

export abstract class UIProfileController {
    username: string

    readonly profileLinkController: UIProfileLinkController
    private overviewController: UIProfileOverviewController
    private lastKnownUsername: string | undefined

    constructor() {
        this.profileLinkController = new UIProfileLinkController()

        ClientUserState.registerChangeListener(() => {
            if(this.username == undefined) return
            if(ClientUserState.userState == undefined) return
            if(ClientUserState.userState.username != this.username) return
            void this.getStickyPlayerInfo()
        })

        this.lastKnownUsername = ClientUserState.userState?.username

        ClientUserState.registerChangeListener(() => {
            const newUsername = ClientUserState.userState?.username
            if(newUsername == undefined) return
            if(newUsername == this.lastKnownUsername) return

            const wasViewingOwnProfile = this.username.toLowerCase() == this.lastKnownUsername?.toLowerCase()
            this.lastKnownUsername = newUsername

            if(!wasViewingOwnProfile) return
            this.refreshProfileForUsernameChange(newUsername)
        })
    }

    protected refreshProfileForUsernameChange(newUsername: string) {
        this.username = newUsername
        this.profileLinkController.setUsername(newUsername)
        this.profileLinkController.resetApiCalls()
        void this.makeApiCall()
        void this.getStickyPlayerInfo()
    }

    protected init() {
        this.setOnClickHandlers()

        const defaultSectionIdx = 0
        const sectionFromHash = this.profileLinkController.showSectionFromHash()
        if(sectionFromHash == false) this.profileLinkController.showSection(defaultSectionIdx)

        void this.makeApiCall()
        void this.getStickyPlayerInfo()

        this.overviewController = new UIProfileOverviewController()
    }

    private setOnClickHandlers() {
        this.profileLinkController.sectionLinks.forEach((link, idx) => {
            link.hasMadeAPICall = false
            setElementOnClick(link, () => {
                this.setLinkClickAction(link, idx)
            }, true)
            if(UIUtilsEjsVariables.isUserOnMobile) link.classList.add('no-hover')
        })
    }

    protected setLinkClickAction(link: ProfileSectionLink, idx: number) {
        this.profileLinkController.activeLink = link
        this.profileLinkController.showSection(idx)
        void this.makeApiCall()
    }

    abstract getUsername(): string
    abstract setStickyUsername(username: string): void
    abstract setUserIcon(iconImage: LobbyImage | undefined): void
    abstract setMembershipIcon(membershipType: ColonistMembershipType | undefined): void
    abstract accountDoesNotExist(): void
    abstract setHistory(historyData: AjaxDataTypes.ProfileHistoryData): void
    abstract setRankedSeasons(seasons: AjaxDataTypes.ProfileDataRankedSeasonDropdown[]): void
    abstract setProfileSettings(userLogins: UserLoginState[]): void

    // eslint-disable-next-line class-methods-use-this
    setItems(items: BaseStoreItem[]): void {
        const itemsDiv = document.getElementById('profile_items_div') as HTMLDivElement
        setElementInnerHTML(itemsDiv, {key: 'strings:colonistCommon.utils.emptyString'})
        for(const item of items) {
            const storeCategory = getStoreCategoryString(item.category)
            const container = createLinkIn(itemsDiv, `/store#${storeCategory}`, {key: 'strings:colonistCommon.utils.emptyString'}, '')
            createImgIn(container, item.image)
            createHeaderIn(container, item.name, '', '', 'H4')
        }
    }

    async getStickyPlayerInfo() {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserProfile.stickyPlayerInfo(this.username), undefined)
        if(response == undefined) {
            this.accountDoesNotExist()
            return
        }
        const stickyPlayerInfo = response.json as AjaxDataTypes.PublicProfileDataStickyPlayerInfo
        const isOwnProfile = ClientUserState.userState != undefined && ClientUserState.userState.username == this.username
        const icon = isOwnProfile ? ClientUserState.userState?.icon : stickyPlayerInfo.iconImageId
        this.setUserIcon(icon)
        this.setMembershipIcon(stickyPlayerInfo.currentMembershipType)
        this.setStickyUsername(stickyPlayerInfo.username)
    }

    async makeApiCall() {
        switch(this.profileLinkController.activeLink.id) {
            case this.profileLinkController.overviewSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.PublicProfileDataOverview>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfile.overview(this.username), (data: AjaxDataTypes.PublicProfileDataOverview) => {
                    this.overviewController.setStats(data)
                })
                break
            case this.profileLinkController.historySectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.ProfileHistoryData>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfile.history(this.username), (data: AjaxDataTypes.ProfileHistoryData) => {
                    this.setHistory(data)
                })
                break
            case this.profileLinkController.itemsSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<BaseStoreItem[]>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfile.items(this.username), (items: BaseStoreItem[]) => {
                    this.setItems(items)
                })
                break
            case this.profileLinkController.rankedSectionLink.id:
                // TODO: Fix this
                // this.rankedController = new UIProfileRankedController(this.username)
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.ProfileDataRankedSeasonDropdown[]>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfileRankedSeasons(), (seasons: AjaxDataTypes.ProfileDataRankedSeasonDropdown[]) => {
                    this.setRankedSeasons(seasons)
                })
                break
            case this.profileLinkController.friendsSectionLink?.id:
                break
            case this.profileLinkController.settingsSectionLink?.id:
                await UIProfileLinkController.genericApiCallWrapper<UserLoginState[]>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserLoginState(), (userLogins: UserLoginState[]) => {
                    this.setProfileSettings(userLogins)
                })
                break
            default:
                break
        }
    }
}
